import axios from "axios";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

// Auth interceptor - add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && window.location.pathname.startsWith("/admin")) {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_user");
    }
    return Promise.reject(err);
  }
);

// Auth
export const loginApi = (data) => api.post("/auth/login", data).then(r => r.data);
export const fetchMe = () => api.get("/auth/me").then(r => r.data);
export const changePasswordApi = (data) => api.post("/auth/change-password", data).then(r => r.data);
export const changeUsernameApi = (data) => api.post("/auth/change-username", data).then(r => r.data);

// Categories
export const fetchCategories = () => api.get("/categories").then(r => r.data);
export const createCategoryApi = (data) => api.post("/categories", data).then(r => r.data);
export const updateCategoryApi = (id, data) => api.patch(`/categories/${id}`, data).then(r => r.data);

export const deleteCategoryApi = (id) => api.delete(`/categories/${id}`).then(r => r.data);

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteCategoryApi(id),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

// Collections
export const fetchCollections = () => api.get("/collections").then(r => r.data);
export const createCollectionApi = (data) => api.post("/collections", data).then(r => r.data);
export const updateCollectionApi = (id, data) => api.patch(`/collections/${id}`, data).then(r => r.data);
export const deleteCollectionApi = (id) => api.delete(`/collections/${id}`).then(r => r.data);

export const useListCollections = () => useQuery({ queryKey: ["collections"], queryFn: fetchCollections });
export const useCreateCollection = () => useMutation({ mutationFn: createCollectionApi });
export const useUpdateCollection = () => useMutation({ mutationFn: ({ id, data }) => updateCollectionApi(id, data) });

export function useDeleteCollection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteCollectionApi(id),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

// Paintings
export const fetchPaintings = (params = {}) => api.get("/paintings", { params }).then(r => r.data);
export const fetchFeaturedPaintings = () => api.get("/paintings/featured").then(r => r.data);
export const fetchOnSalePaintings = () => api.get("/paintings/on-sale").then(r => r.data);
export const fetchRecentPaintings = () => api.get("/paintings/recent").then(r => r.data);
export const fetchPainting = (id) => api.get(`/paintings/${id}`).then(r => r.data);
export const createPaintingApi = (data) => api.post("/paintings", data).then(r => r.data);
export const updatePaintingApi = (id, data) => api.patch(`/paintings/${id}`, data).then(r => r.data);
export const deletePaintingApi = (id) => api.delete(`/paintings/${id}`);
export const toggleFeaturedApi = (id) => api.patch(`/paintings/${id}/toggle-featured`).then(r => r.data);
export const toggleSaleApi = (id) => api.patch(`/paintings/${id}/toggle-sale`).then(r => r.data);

// Gallery summary
export const fetchGallerySummary = () => api.get("/gallery/summary").then(r => r.data);

// Health
export const fetchHealth = () => api.get("/health").then(r => r.data);

// Orders
export const createOrderApi = (data) => api.post("/orders", data).then(r => r.data);
export const fetchOrders = () => api.get("/orders").then(r => r.data);
export const fetchOrderStats = () => api.get("/orders/stats").then(r => r.data);
export const updateOrderStatusApi = (id, status) => api.patch(`/orders/${id}/status`, { status }).then(r => r.data);
export const cancelOrderApi = (id) => api.post(`/orders/${id}/cancel`).then(r => r.data);

// ── Bold ─────────────────────────────────────────────────────────────────
export const fetchBoldConfig = () => api.get("/config/bold").then(r => r.data);
export const useBoldConfig = () => useQuery({ queryKey: ["bold-config"], queryFn: fetchBoldConfig });
export const createBoldOrder = (data) => api.post("/bold/create-checkout", data).then(r => r.data);
export const getBoldTransactionStatus = (boldOrderId) =>
  api.get(`/bold/status/${boldOrderId}`).then(r => r.data);
// ─────────────────────────────────────────────────────────────────────────

// React Query Hooks
export const useListCategories = () => useQuery({ queryKey: ["categories"], queryFn: fetchCategories });
export const useCreateCategory = () => useMutation({ mutationFn: createCategoryApi });
export const useUpdateCategory = () => useMutation({ mutationFn: ({ id, data }) => updateCategoryApi(id, data) });

export const useListPaintings = (params) => useQuery({ queryKey: ["paintings", params], queryFn: () => fetchPaintings(params) });
export const useListFeaturedPaintings = () => useQuery({ queryKey: ["paintings", "featured"], queryFn: fetchFeaturedPaintings });
export const useListOnSalePaintings = () => useQuery({ queryKey: ["paintings", "on-sale"], queryFn: fetchOnSalePaintings });
export const useListRecentPaintings = () => useQuery({ queryKey: ["paintings", "recent"], queryFn: fetchRecentPaintings });
export const useGetPainting = (id) => useQuery({ queryKey: ["painting", id], queryFn: () => fetchPainting(id), enabled: !!id });
export const useCreatePainting = () => useMutation({ mutationFn: createPaintingApi });
export const useUpdatePainting = () => useMutation({ mutationFn: ({ id, data }) => updatePaintingApi(id, data) });
export const useDeletePainting = () => useMutation({ mutationFn: (id) => deletePaintingApi(id) });
export const useToggleFeatured = () => useMutation({ mutationFn: (id) => toggleFeaturedApi(id) });
export const useToggleSale = () => useMutation({ mutationFn: (id) => toggleSaleApi(id) });

export const useGetGallerySummary = () => useQuery({ queryKey: ["gallery-summary"], queryFn: fetchGallerySummary });
export const useHealthCheck = () => useQuery({ queryKey: ["health"], queryFn: fetchHealth });
export const useCreateOrder = () => useMutation({ mutationFn: createOrderApi });
export const useListOrders = () => useQuery({ queryKey: ["orders"], queryFn: fetchOrders });
export const useOrderStats = () => useQuery({ queryKey: ["order-stats"], queryFn: fetchOrderStats });
export const useUpdateOrderStatus = () => useMutation({ mutationFn: ({ id, status }) => updateOrderStatusApi(id, status) });

// WhatsApp config (public)
export const fetchWhatsappConfig = () => api.get("/config/whatsapp").then(r => r.data);
export const useWhatsappConfig = () => useQuery({ queryKey: ["whatsapp-config"], queryFn: fetchWhatsappConfig });

// Email settings
export const fetchEmailConfig = () => api.get("/settings/email").then(r => r.data);
export const updateEmailConfigApi = (data) => api.put("/settings/email", data).then(r => r.data);
export const sendTestEmailApi = (email) => api.post("/settings/email/test", { email }).then(r => r.data);
export const useEmailConfig = () => useQuery({ queryKey: ["email-config"], queryFn: fetchEmailConfig });
export const useUpdateEmailConfig = () => useMutation({ mutationFn: updateEmailConfigApi });
export const useSendTestEmail = () => useMutation({ mutationFn: sendTestEmailApi });